import { InfoIcon } from '@assets/icons'
import { EmptyTable, type EmptyTableProps } from '@components/EmptyTable'
import { IconWrap } from '@components/IconWrap'
import { SelectIcon } from '@components/SelectIcon'
import { SortByOrderingEnum } from '@helpers/enums'
import { LINE } from '@helpers/specialChars'
import {
	Table as MuiTable,
	TableBody as MuiTableBody,
	type TableBodyProps,
	TableCell as MuiTableCell,
	type TableCellProps as MuiTableCellProps,
	TableContainer as MuiTableContainer,
	type TableContainerProps,
	TableHead as MuiTableHead,
	type TableHeadProps,
	type TableProps,
	TableRow as MuiTableRow,
	type TableRowProps,
	TableSortLabel,
	Typography,
	useTheme,
} from '@mui/material'
import { type CSSProperties, type ReactNode, useCallback } from 'react'

type TLowerCaseSortDirection = 'asc' | 'desc'

const HeaderCell = ({
	id,
	label,
	tooltipLabel,
	labelColor,
	sortable,
	selectAll,
	style,
	sortBy,
	sortOrder,
	handleSortClick,
	...props
}: {
	id: string
	label: string
	tooltipLabel?: string | ReactNode
	labelColor: string
	sortable: boolean
	selectAll?: ReactNode
	style: CSSProperties
	sortBy?: string
	sortOrder: TLowerCaseSortDirection
	handleSortClick: (id: string) => void
	minWidth?: string
}) => {
	const onSortClick = useCallback(() => {
		handleSortClick(id)
	}, [handleSortClick, id])

	return (
		<TableHeaderCell
			{...props}
			style={{ ...style, color: labelColor }}
			sx={{ padding: sortable ? 0 : 2 }}
			sortDirection={sortBy === id ? sortOrder : false}
		>
			{sortable ? (
				<TableSortLabel
					active={sortBy === id}
					onClick={onSortClick}
					direction={sortBy === id ? sortOrder : 'asc'}
					sx={{ p: 2, width: '100%' }}
				>
					{selectAll}
					{label}
				</TableSortLabel>
			) : (
				<>
					{selectAll}
					{label}
				</>
			)}

			{tooltipLabel !== undefined && (
				<IconWrap label={tooltipLabel}>
					<InfoIcon />
				</IconWrap>
			)}
		</TableHeaderCell>
	)
}

type EnhandedTableContainerProps = TableContainerProps &
	EmptyTableProps & { showEmptyTable?: boolean }

const TableContainer = ({
	style,
	title,
	subtitle,
	ctaText,
	ctaCallback,
	secondaryAction,
	showEmptyTable,
	...props
}: EnhandedTableContainerProps) => {
	const { spaces } = useTheme()

	if (showEmptyTable) {
		return (
			<EmptyTable
				title={title}
				subtitle={subtitle}
				ctaText={ctaText}
				ctaCallback={ctaCallback}
				secondaryAction={secondaryAction}
			/>
		)
	}

	return (
		<MuiTableContainer style={{ borderRadius: spaces.sm, ...style }} {...props}>
			{props.children}
		</MuiTableContainer>
	)
}

const Table = (props: TableProps) => (
	<MuiTable {...props}>{props.children}</MuiTable>
)

const TableHead = ({ style, ...props }: TableHeadProps) => {
	const { palette } = useTheme()

	return (
		<MuiTableHead
			style={{
				backgroundColor: palette.borderColors.darkestGray,
				borderRadius: '12px 12px 0px 0px',
				borderBottom: `2px solid ${palette.backgroundColors.black}`,
				...style,
			}}
			{...props}
		>
			{props.children}
		</MuiTableHead>
	)
}

const TableBody = (props: TableBodyProps) => (
	<MuiTableBody {...props}>{props.children}</MuiTableBody>
)

const TableRow = (props: TableRowProps) => (
	<MuiTableRow {...props}>{props.children}</MuiTableRow>
)

interface ITableCellProps extends MuiTableCellProps {
	minWidth?: string
	isSelected?: boolean
}

const TableCell = ({
	style,
	minWidth,
	isSelected,
	...props
}: ITableCellProps) => {
	const { palette } = useTheme()

	return (
		<MuiTableCell
			style={{
				minWidth,
				background: '#262626',
				border: `1px solid ${palette.backgroundColors.opaque}`,
				...(isSelected && {
					background: 'rgba(68, 197, 113, 0.5)',
				}),
				...style,
			}}
			{...props}
		>
			{props.children}
		</MuiTableCell>
	)
}

const TableHeaderCell = ({ style, minWidth, ...props }: ITableCellProps) => {
	const { palette } = useTheme()
	return (
		<TableCell
			style={{
				minWidth,
				borderTop: 'none',
				borderBottom: `1px solid ${palette.borderColors.darkGray}`,
				background: '#393939',
				...style,
			}}
			{...props}
		>
			<Typography style={{ fontSize: '0.875rem', fontWeight: '600' }}>
				{props.children}
			</Typography>
		</TableCell>
	)
}

interface ITableCellTypographyProps extends ITableCellProps {
	children?: React.ReactNode
}

const TableCellTypography = ({
	children,
	minWidth,
	...props
}: ITableCellTypographyProps) => (
	<TableCell
		{...props}
		style={{ userSelect: 'none', minWidth, ...props.style }}
	>
		<Typography variant="body2">{children ?? LINE}</Typography>
	</TableCell>
)

export interface IEnhancedTableHeadCellProps extends ITableCellProps {
	id: string
	label: string
	sticky?: boolean
	sublabel?: string
	sortable?: boolean
	labelColor?: string
	tooltipLabel?: string | ReactNode
	selectAll?: ReactNode | undefined
}

interface IEnhancedTableProps extends ITableCellProps {
	sortBy?: string
	sortOrder?: SortByOrderingEnum | undefined
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleSort: (id: any) => void
	headCells: readonly IEnhancedTableHeadCellProps[]
}

type TConditionalProps =
	| {
			isSelectable?: true
			isSelected: boolean
			selectTooltip?: string
			onSelectAllClick: () => void
			selectableCellsLabel?: never
	  }
	| {
			isSelectable?: false
			isSelected?: never
			selectTooltip?: never
			onSelectAllClick?: never
			selectableCellsLabel?: string
	  }

const EnhancedTableHead = ({
	sortBy,
	headCells,
	handleSort,
	isSelected,
	selectTooltip,
	style: _style,
	isSelectable = true,
	sortOrder: _sortOrder = SortByOrderingEnum.Desc,
	selectableCellsLabel,
	onSelectAllClick,
	...props
}: IEnhancedTableProps & TConditionalProps) => {
	const borderBottom = '1px solid #d4d4d4'
	const sortOrder = _sortOrder.toLowerCase() as TLowerCaseSortDirection

	const style: CSSProperties = {
		..._style,
		borderBottom,
	}

	const handleSortClick = useCallback(
		(id: string) => {
			handleSort(id)
		},
		[handleSort]
	)

	return (
		<TableHead>
			<TableRow>
				{isSelectable && onSelectAllClick && isSelected !== undefined ? (
					<TableHeaderCell
						{...props}
						style={{ ...style, maxWidth: '100px', width: '85px' }}
					>
						<SelectIcon
							label={selectTooltip}
							isSelected={isSelected}
							onClick={onSelectAllClick}
							condition={selectTooltip !== undefined}
						/>
					</TableHeaderCell>
				) : selectableCellsLabel ? (
					<TableCellTypography {...props} style={{ borderBottom }}>
						{selectableCellsLabel}
					</TableCellTypography>
				) : null}

				{headCells.map(
					({
						id,
						label,
						tooltipLabel,
						labelColor = '#FFF',
						sortable = true,
						selectAll,
						...props
					}) => (
						<HeaderCell
							key={id}
							id={id}
							label={label}
							tooltipLabel={tooltipLabel}
							labelColor={labelColor}
							sortable={sortable}
							selectAll={selectAll}
							style={style}
							sortBy={sortBy}
							sortOrder={sortOrder}
							handleSortClick={handleSortClick}
							{...props}
						/>
					)
				)}
			</TableRow>
		</TableHead>
	)
}

export {
	EnhancedTableHead,
	Table,
	TableBody,
	TableCell,
	TableCellTypography,
	TableContainer,
	TableHead,
	TableHeaderCell,
	TableRow,
}
